<template>
  <div id="integrationTest" :class="[mobile ? 'productShopMoble' : '', isTurquoise ? 'turquoiseTheme' : '', translationMode ? 'isTranslationMode' : '', this.$root.admin ? 'hasAdminSettings' : '']">
    <p class="heading integrationTestSettings" v-if="this.$root.admin">
        <svg class="fontAwesomeIcon" :class="['settings', settingsMode ? 'active' : '']" @click="settingsMode = !settingsMode" v-if="this.$root.admin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"/></svg>
    </p>
    
    <settingsComponent @saveSettings="setSettings" v-if="settingsMode && this.$root.admin"></settingsComponent>

    <!-- Toolbar: sits above both filterContainer and pricelistContainer -->
    <div class="priceListToolBar">
        <div class="horizontalLinkBoxesSlot">
          <horizontalLinkBoxes />
        </div>
        <button
          v-if="this.$root.settings.showFilter && mobile"
          class="mobileFilterBtn"
          @click="mobileFilterOpen = true"
        >
            <i class="fas fa-sliders-h"></i>
            <span>{{ $root.language.wsm_Filter || 'Filter' }}</span>
            <span v-if="$store.state.priceListFilterPills.length > 0" class="filterBadge">
              {{ $store.state.priceListFilterPills.length }}
            </span>
        </button>
        <div v-if="!mobile" class="viewToggle">
            <button :class="['listButton', 'sortBtn', view == 'list' ? 'active' : '']" @click="view = 'list'"><i class="fas fa-bars"></i></button>
            <button :class="['gridButton', 'sortBtn', view == 'grid' ? 'active' : '']" @click="view = 'grid'"><i class="fas fa-th"></i></button>
        </div>
    </div>

    <filterList v-if="this.$root.settings.showFilter" ref="desktopFilter" :hide-filters="mobile" />
    <!--
      Filter off still needs the sidebar when the CMS left column has content.
      The host is the same 27% slot the filter uses, so the price list stays at 71%
      instead of stretching to 100% beside a page2cols / product-left-box float.
    -->
    <div v-else-if="leftColumnActive" class="filterContainer" ref="leftColumnHost">
      <div ref="relocatedLeftBox" class="relocatedLeftBox"></div>
    </div>
    <priceList :view="view" :constrain-width="leftColumnActive"></priceList>

    <!--
      Mobile filter drawer.
      On mobile, the sidebar filter is hidden and replaced by this bottom-sheet drawer.
      - Overlay: dims background, tap to dismiss
      - Drawer: slides up from bottom, swipe handle or content (when scrolled to top) to dismiss
      - The filterList inside uses v-if (not v-show) so it stays mounted and retains checkbox state
    -->
    <transition name="drawer-fade">
      <div
        class="mobileFilterOverlay"
        v-if="mobileFilterOpen"
        @click.self="mobileFilterOpen = false"
      ></div>
    </transition>

    <div
      class="mobileFilterDrawer"
      :class="{ open: mobileFilterOpen }"
      :style="drawerSwipeStyle"
    >
      <!-- Drag handle: always triggers swipe-to-dismiss -->
      <div
        class="drawerHandle"
        @click="mobileFilterOpen = false"
        @touchstart="onDrawerTouchStart"
        @touchmove="onDrawerTouchMove"
        @touchend="onDrawerTouchEnd"
      >
        <span class="drawerHandleBar"></span>
      </div>

      <!-- Scrollable filter content: only triggers swipe-to-dismiss when scrolled to top -->
      <div
        class="drawerContent"
        ref="drawerContent"
        @touchstart="onContentTouchStart"
        @touchmove="onContentTouchMove"
        @touchend="onDrawerTouchEnd"
      >
        <filterList
          v-if="this.$root.settings.showFilter"
          ref="mobileFilter"
          :show-counts="true"
          :filters-only="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
  //const priceList = ()=> import("./components/pricelist");
  import priceList from "./components/pricelist";
  import filterList from "./components/productFilter.vue";
  import horizontalLinkBoxes from "./components/horizontalLinkBoxes.vue";

  // const filterList = () =>  import ('./components/productFilter');
  //import 'element-closest-polyfill'; 
  export default {
    name: 'IntegrationTest',
    components: {
      priceList,
      filterList,
      horizontalLinkBoxes,
      settingsComponent: () => window.shopPageObj.$root.admin ?
        import ('./components/settings.vue') : '',
    },
    data() {
      return {
        view: this.$root.settings.defaultView,
        settingsMode: false,
        saving: false,
        mobile: false,
        zoomLvl: 1,
        isTurquoise: this.$root.isTurquoisePointshop,
        translationMode: Boolean(window.translationMode),

        // Mobile filter drawer state
        mobileFilterOpen: false,
        savedScrollY: 0,           // preserves scroll position while body is locked
        drawerTouchStartY: 0,      // Y coordinate where touch began
        drawerTouchCurrentY: 0,    // Y coordinate of current touch position
        drawerIsDragging: false,    // true when a swipe-to-dismiss gesture is active
        // True when Filter is off but a CMS left column should sit in the sidebar.
        leftColumnActive: false,
      }
    },
    created() {
      this.leftColumnActive = !this.$root.settings.showFilter && this.hasExternalLeftColumn();
    },
    computed: {
      /**
       * While the user is swiping the drawer down, apply a live translateY
       * offset so the drawer follows their finger. Only allows downward dragging.
       */
      drawerSwipeStyle() {
        if (!this.drawerIsDragging) return {};
        var delta = this.drawerTouchCurrentY - this.drawerTouchStartY;
        if (delta < 0) delta = 0;
        return { transform: 'translateY(' + delta + 'px)', transition: 'none' };
      },
    },
    watch: {
      /**
       * Lock/unlock background scroll when the drawer opens/closes.
       * Uses position:fixed on body — the only reliable method on iOS Safari
       * where overflow:hidden alone doesn't prevent scroll.
       */
      mobileFilterOpen(val) {
        if (val) {
          this.savedScrollY = window.scrollY;
          Object.assign(document.body.style, {
            position: 'fixed',
            top: '-' + this.savedScrollY + 'px',
            left: '0',
            right: '0',
            overflow: 'hidden',
          });
        } else {
          Object.assign(document.body.style, {
            position: '',
            top: '',
            left: '',
            right: '',
            overflow: '',
          });
          window.scrollTo(0, this.savedScrollY);
        }
      }
    },
    methods: {
      setSettings: function(options) {
        location.reload();
      },
      getPoints: function() {
        this.$store.dispatch("updatePointShopWidget");
      },
      /**
       * True when Filter is off and a CMS column next to the shop still has content.
       * Covers .product-left-box and the sibling column of a .page2cols wrapper.
       */
      hasExternalLeftColumn: function() {
        if (this.productLeftBoxes().length) return true;
        return !!this.page2colsSiblingColumn();
      },
      columnHasContent: function(node) {
        if (!node) return false;
        for (var i = 0; i < node.childNodes.length; i++) {
          var child = node.childNodes[i];
          if (child.nodeType === 1) return true;
          if (child.nodeType === 3 && child.textContent.trim()) return true;
        }
        return false;
      },
      productLeftBoxes: function() {
        var boxes = document.querySelectorAll('#content > .product-left-box');
        var result = [];
        for (var i = 0; i < boxes.length; i++) {
          if (this.columnHasContent(boxes[i])) result.push(boxes[i]);
        }
        return result;
      },
      /**
       * The .page2cols column that does not contain this shop.
       * Returns null when the shop is not inside a 2-column CMS wrapper.
       */
      page2colsSiblingColumn: function() {
        var root = this.$el && this.$el.closest ? this.$el : document.getElementById('integrationTest');
        if (!root || !root.closest) return null;
        var page = root.closest('.page2cols');
        if (!page) return null;
        var shopCol = root.parentElement;
        while (shopCol && shopCol !== page && !shopCol.classList.contains('col1') && !shopCol.classList.contains('col2')) {
          shopCol = shopCol.parentElement;
        }
        if (!shopCol || shopCol === page) return null;
        var siblingClass = shopCol.classList.contains('col1') ? 'col2' : 'col1';
        var sibling = null;
        for (var i = 0; i < page.children.length; i++) {
          if (page.children[i].classList.contains(siblingClass)) {
            sibling = page.children[i];
            break;
          }
        }
        if (!sibling || sibling.contains(root) || !this.columnHasContent(sibling)) return null;
        return { page: page, shopCol: shopCol, sibling: sibling };
      },
      moveNodeChildren: function(node, target) {
        var moved = false;
        var child = node.firstChild;
        while (child) {
          var next = child.nextSibling;
          if (child.nodeType === 1 && child.classList.contains('product-left-box')) {
            while (child.firstChild) {
              target.appendChild(child.firstChild);
              moved = true;
            }
            child.style.display = 'none';
          } else {
            target.appendChild(child);
            moved = true;
          }
          child = next;
        }
        return moved;
      },
      /**
       * Move the CMS left column into the sidebar, then hide the empty box
       * so it does not stay as a floated column beside the shop.
       * With Filter on, the host is the filter sidebar. With Filter off, it is
       * the leftColumnHost rendered for the same 27/71 split.
       */
      relocateProductLeftBox: function() {
        var filterComp = this.$refs.desktopFilter;
        var target = (filterComp && filterComp.$refs.relocatedLeftBox) || this.$refs.relocatedLeftBox;
        if (!target) return;

        var moved = false;
        var boxes = document.querySelectorAll('#content > .product-left-box');
        for (var i = 0; i < boxes.length; i++) {
          if (this.moveNodeChildren(boxes[i], target)) moved = true;
          boxes[i].style.display = 'none';
        }

        if (this.leftColumnActive) {
          var split = this.page2colsSiblingColumn();
          if (split) {
            if (this.moveNodeChildren(split.sibling, target)) moved = true;
            split.sibling.style.display = 'none';
            split.shopCol.style.width = '100%';
            split.shopCol.style.float = 'none';
          }
        }

        if (moved && filterComp) {
          filterComp.hasRelocatedContent = true;
        }
      },

      // --- Drawer swipe-to-dismiss handlers ---
      // The drag handle always initiates a drag immediately.
      onDrawerTouchStart: function(e) {
        this.drawerTouchStartY = e.touches[0].clientY;
        this.drawerTouchCurrentY = e.touches[0].clientY;
        this.drawerIsDragging = true;
      },
      // The scrollable content area only initiates a drag when
      // the user is at the top and pulling down.
      onContentTouchStart: function(e) {
        this.drawerTouchStartY = e.touches[0].clientY;
        this.drawerTouchCurrentY = e.touches[0].clientY;
        this.drawerIsDragging = false;
      },
      onContentTouchMove: function(e) {
        var scrollTop = this.$refs.drawerContent ? this.$refs.drawerContent.scrollTop : 0;
        var delta = e.touches[0].clientY - this.drawerTouchStartY;
        if (scrollTop <= 0 && delta > 0) {
          this.drawerIsDragging = true;
        }
        if (!this.drawerIsDragging) return;
        this.drawerTouchCurrentY = e.touches[0].clientY;
      },
      onDrawerTouchMove: function(e) {
        if (!this.drawerIsDragging) return;
        this.drawerTouchCurrentY = e.touches[0].clientY;
      },
      // Close the drawer if the user swiped down more than 80px
      onDrawerTouchEnd: function() {
        if (!this.drawerIsDragging) return;
        var delta = this.drawerTouchCurrentY - this.drawerTouchStartY;
        this.drawerIsDragging = false;
        if (delta > 80) {
          this.mobileFilterOpen = false;
        }
      },
    },
    mounted() {
      this.$store.state.pointshop = this.$root.pointshop
      this.mobile = (window.innerWidth <= 700)
      this.$store.dispatch('screenSize', (window.innerWidth <= 700))

      //mobile stuff
      window.onresize = (() => {
        this.$store.dispatch('screenSize', (window.innerWidth <= 700))
      });
      this.$store.subscribe((mutation, state) => {
        switch (mutation.type) {
          case 'setMobile':
            this.mobile = mutation.payload
            break;
        }
      })
      var _that = this;
      document.querySelector('#baseTplContentWrapper').addEventListener('click', function(e) {
        if (e.target.previousElementSibling != null && e.target.previousElementSibling.id == 'CampaignCodeInputDropDown') {
          _that.$store.dispatch("getPriceList", !_that.$root.settings.showFilter ? [_that.$root.settings.showCat,_that.$root.settings.hideCat] : [[],[]]);
        }
      }, true);

      //presearch
      var url_string = window.location.href; //window.location.href
      var url = new URL(url_string);
      var preSearch = url.searchParams.get("presearch");
      if(preSearch != null) {
        this.$store.dispatch("collectItemsFromSearchQuery", preSearch)
      }

      this.$nextTick(() => {
        this.relocateProductLeftBox();
      });
    }
  };
</script>

<style lang="scss" scoped>
  #integrationTest {
    text-align: center;
    position: relative;
    display: flow-root;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    &.hasAdminSettings .priceListToolBar {
      padding-right: 28px;
    }
  }
  .integrationTestSettings{
    position: absolute;
    right: 0px;
    top: 0;
    float: none;
    z-index: 2;
  }
  .heading {
    color: #004570;
    cursor: pointer;
    margin: 0;
  }
  .filterContainer {
    width: 27%;
    display: inline-block;
    vertical-align: top;
    float: left;
    min-width: 0;
    box-sizing: border-box;
  }

  /* --- Price list toolbar (above both filter sidebar and product list) --- */
  .priceListToolBar {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 10px;
    clear: both;
  }
  .horizontalLinkBoxesSlot {
    text-align: left;
    flex: 1 1 auto;
    min-width: 0;
  }
  .mobileFilterBtn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #004570;
    color: white;
    border: none;
    border-radius: 22px;
    padding: 8px 16px;
    font-size: 0.9em;
    font-weight: 600;
    cursor: pointer;
    position: relative;
    white-space: nowrap;
    margin-bottom:5px;
    flex: 0 0 auto;
    &:hover {
      background: #035080;
    }
    span { color: white; }
    i { font-size: 0.9em; color: white; }
    .filterBadge {
      background: #7caf63;
      color: white;
      font-size: 0.75em;
      font-weight: 700;
      min-width: 20px;
      height: 20px;
      border-radius: 10px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-left: 2px;
    }
  }
  .viewToggle {
    text-align: right;
    flex: 0 0 auto;
    width: auto;
    margin: 5px;
    .listButton {
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;
      border-left: none;
      border-right: none;
    }
    .gridButton {
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
      border-right: none;
      border-left: none;
    }
    .sortBtn {
      padding: 5px;
      background: white;
      width: 43px;
      border: 1px solid gray;
      height: 43px;
      transition: all 0.2s ease-in-out;
      cursor: pointer;
      &:hover:not(.active) {
        background: #00457029;
        i { color: #004570; }
      }
      i { color: #004570; }
    }
    .gridButton:not(.active) { border-left: none; }
    .listButton:not(.active) { border-right: none; }
    .active {
      background: #7caf63 !important;
      border: 1px solid #7caf63;
      i { color: white; }
    }
  }

  /* Mobile toolbar overrides */
  .productShopMoble {
    .priceListToolBar {
      flex-wrap: nowrap;
      align-items: flex-end;
      gap: 10px;
      margin-bottom: 16px;
    }
    .horizontalLinkBoxesSlot {
      flex: 1 1 auto;
      min-width: 0;
      width: auto;
    }
    .mobileFilterBtn {
      flex-shrink: 0;
      height: 33px;
      margin-bottom: 0;
      border-radius: 4px;
      padding: 0 14px;
    }
  }

  /* Pointshop turquoise theme */
  .turquoiseTheme {
    .mobileFilterBtn {
      background: #418a90;
      &:hover { background: #4a9ca3; }
    }
  }

  /* --- Mobile filter drawer --- */

  /* Dark overlay behind the drawer, tap to dismiss */
  .mobileFilterOverlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 999;
  }
  .drawer-fade-enter-active,
  .drawer-fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .drawer-fade-enter,
  .drawer-fade-leave-to {
    opacity: 0;
  }

  /* Bottom-sheet drawer: hidden offscreen by default, slides up when .open */
  .mobileFilterDrawer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: #fff;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.18);
    max-height: 90vh;
    max-height: 90dvh; /* dynamic viewport height — adapts to mobile browser chrome */
    transform: translateY(100%);
    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    will-change: transform;
    display: flex;
    flex-direction: column;
    &.open {
      transform: translateY(0);
    }
  }

  /* Swipe handle bar at top of drawer */
  .drawerHandle {
    display: flex;
    justify-content: center;
    padding: 14px 0 10px;
    cursor: pointer;
    flex-shrink: 0;
    touch-action: none; /* prevents browser from hijacking touch for scroll/zoom */
  }
  .drawerHandleBar {
    width: 40px;
    height: 5px;
    border-radius: 3px;
    background: #ccc;
  }

  /* Scrollable area inside the drawer */
  .drawerContent {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 8px 16px 24px;
    flex: 1;
    min-height: 0;
  }
</style>

<style lang="scss">
  .contentelement-admin > #integrationTest {
    clear: both;
    float: none;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  #integrationTest img {
    display: block !important;
  }
  #integrationTest .productImage img {
    max-width: 100%;
  }
  #integrationTest{
    .fontAwesomeIcon{
      width: 15px;
      &.white{
        fill: white;
      }
      &.iconSpin{
        animation: spinSteps 1s steps(8) infinite;
      } 
    }
    @keyframes spinSteps {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  }

  @media screen and (max-width: 700px) {
  #integrationTest{
    .hideInMobile{
      display: none;
    }
  }
}
@media screen and (min-width: 700px) {
  #integrationTest{
    .hideInDesktop{
      display: none;
    }
  }  
}
/* Override filterContainer width inside the drawer (normally 27% for sidebar) */
.mobileFilterDrawer .drawerContent .filterContainer {
  width: 100%;
  float: none;
}
#integrationTest .relocatedLeftBox {
  min-width: 0;
  max-width: 100%;
  text-align: left;
  /* The CMS box is a 287px float on US. Inside the sidebar it has to shrink with the column. */
  .product-left-box {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    float: none !important;
    clear: none;
  }
  .contentelement,
  .infobox,
  .infoboxHeader,
  .infoboxBody {
    width: auto;
    max-width: 100%;
    min-width: 0;
    float: none;
    box-sizing: border-box;
  }
  img {
    max-width: 100% !important;
    float: none;
  }
  /* CMS editors set pixel sizes inline; keep the aspect ratio when max-width shrinks them. */
  img[style*="height"] {
    height: auto !important;
  }
}

/* On mobile the sidebar is full width and sits above the product list.
   Flatten it so the relocated CMS block (ingredient thumbnail, etc.)
   can be ordered after the product list, at the bottom of the page. */
@media screen and (max-width: 700px) {
  #integrationTest#integrationTest.productShopMoble {
    display: flex;
    flex-direction: column;
  }
  #integrationTest.productShopMoble > .pricelistContainer {
    width: 100%;
    float: none;
  }
  #integrationTest.productShopMoble > .filterContainer {
    display: contents;
  }
  #integrationTest.productShopMoble > .filterContainer > .relocatedLeftBox {
    order: 1;
    width: 100%;
    margin-top: 24px;
  }
}
</style>